import { useEffect, useState } from "react";
import { Box, Button, Divider } from "@mui/material";
import { defineMessage, FormattedMessage, MessageDescriptor } from "react-intl";
import { useMutation } from "@apollo/client";
import moment from "moment";
import { FieldErrorList } from "@/types";
import { extractFieldErrors } from "@/tools";
import { useDispatch } from "react-redux";
import { setLastMeetingId, showNotification } from "@/actions";
import { Link, useNavigate } from "react-router-dom";
import MeetingForm from "./MeetingForm";
import { SaveMeetingDocument, SaveMeetingMutation, SaveMeetingMutationVariables } from "./queries.generated";
import InvitationFormTable from "./InvitationFormTable";
import { SaveMeetingInput } from "@/api";
import deepEqual from "deep-equal";
import { useFormGuard } from "@/hooks";

type Props = {
    successNotification: MessageDescriptor;
    initialFormData: SaveMeetingInput;
    autoGeneratedInvitationIds?: number[];
};

const SaveMeetingForm = ({ initialFormData, successNotification, autoGeneratedInvitationIds = [] }: Props) => {
    const [formData, setFormData] = useState(initialFormData);
    const [submitDisabled, setSubmitDisabled] = useState(false);
    const [formErrors, setFormErrors] = useState([]);
    const [mutation] = useMutation<SaveMeetingMutation, SaveMeetingMutationVariables>(SaveMeetingDocument);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [meetingStartsNow, setMeetingStartsNow] = useState(false);
    const [pendingPath, setPendingPath] = useState<string | null>(null);

    const formHasModifications = !deepEqual(initialFormData, formData) || meetingStartsNow;

    useFormGuard(formHasModifications);

    // Defer navigation until guard disables (formHasModifications becomes false)
    useEffect(() => {
        if (!formHasModifications && pendingPath) {
            navigate(pendingPath);
            setPendingPath(null);
        }
    }, [formHasModifications, pendingPath, navigate]);

    function updateFormData(partialData: Partial<SaveMeetingInput>) {
        setFormData(Object.assign({}, formData, partialData));
    }

    function resetForm() {
        setFormData(initialFormData);
        setFormErrors([]);
        setMeetingStartsNow(false);
    }

    async function submit() {
        setSubmitDisabled(true);

        const meeting = { ...formData };
        if (meetingStartsNow) delete meeting.startTime;

        const response = await mutation({ variables: { meeting } });
        setSubmitDisabled(false);
        setFormErrors(response.data.saveMeeting.errors || []);

        if (response.data.saveMeeting.errors !== null) {
            dispatch(
                showNotification({
                    message: defineMessage({ id: "formError", defaultMessage: "The form has error(s)!" }),
                    variant: "error",
                }),
            );
            return;
        }

        dispatch(showNotification({ message: successNotification }));
        dispatch(setLastMeetingId(response.data.saveMeeting.id));

        const targetPath = `/meeting/view/${response.data.saveMeeting.id}`;
        resetForm();
        // Navigation is deferred until formHasModifications becomes false
        setPendingPath(targetPath);
    }

    function getFieldErrors(fieldName: keyof SaveMeetingInput): FieldErrorList {
        return extractFieldErrors(formErrors, fieldName);
    }

    const participantFieldErrors = getFieldErrors("invitations");

    return (
        <div style={{ padding: "1em" }}>
            <MeetingForm
                disabled={submitDisabled}
                updateFormData={updateFormData}
                formData={formData}
                getFieldErrors={getFieldErrors}
                meetingStartsNow={meetingStartsNow}
                setMeetingStartsNow={setMeetingStartsNow}
            />
            <InvitationFormTable
                errors={participantFieldErrors}
                formData={formData.invitations}
                updateFormData={(data) => updateFormData({ invitations: data })}
                subTitleId={
                    initialFormData.id
                        ? defineMessage({
                              id: "builtInInvitations.edit",
                              defaultMessage: "Note: auto generated invitations cannot be edited)",
                          })
                        : defineMessage({
                              id: "builtInInvitations.create",
                              defaultMessage: "A general and a host invitation will be created automatically.",
                          })
                }
                autoGeneratedInvitationIds={autoGeneratedInvitationIds}
            />
            <Divider style={{ margin: "1em 0" }} />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={submitDisabled || !formHasModifications}
                    onClick={submit}
                >
                    <FormattedMessage id="save" defaultMessage="Save" />
                </Button>
                <Button
                    sx={{ marginLeft: "1em" }}
                    color="secondary"
                    variant="contained"
                    component={Link}
                    to={initialFormData.id ? `/meeting/view/${initialFormData.id}` : "/"}
                >
                    <FormattedMessage id="cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                    sx={{ marginLeft: "1em" }}
                    color="secondary"
                    variant="contained"
                    disabled={!formHasModifications}
                    onClick={resetForm}
                >
                    <FormattedMessage id="reset" defaultMessage="Reset" />
                </Button>
            </Box>
        </div>
    );
};

export const emptyInitialFormData: SaveMeetingInput = {
    summary: "",
    pinCode: "",
    description: "",
    startTime: moment().second(0).millisecond(0).add(2, "h").toISOString(),
    length: 60,
    invitations: [],
    allowUnmute: true,
};

export default SaveMeetingForm;
